import { useState, type FormEvent, type KeyboardEvent } from 'react';

import type { BlockListItem } from '@flow/sync';

import { t } from '../i18n/es.js';

export interface BlockListProps {
  blocks: BlockListItem[];
  onCreate: (title: string) => Promise<void> | void;
  onRename: (id: string, title: string) => Promise<void> | void;
  onComplete: (block: BlockListItem) => Promise<void> | void;
  onReopen: (block: BlockListItem) => Promise<void> | void;
}

/** Test UI of F0: a flat list of actionable blocks with create, rename and complete. */
export function BlockList({ blocks, onCreate, onRename, onComplete, onReopen }: BlockListProps) {
  const [title, setTitle] = useState('');
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = title.trim();
    if (!value) return;
    setTitle('');
    await onCreate(value);
  }

  async function commitRename() {
    if (!editing) return;
    const value = editing.title.trim();
    const current = blocks.find((b) => b.id === editing.id);
    setEditing(null);
    if (value && current && value !== current.title) await onRename(editing.id, value);
  }

  function handleEditKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') void commitRename();
    if (event.key === 'Escape') setEditing(null);
  }

  return (
    <section aria-labelledby="blocks-title">
      <h2 id="blocks-title">{t.blocks.title}</h2>
      <form className="row" onSubmit={(event) => void handleCreate(event)}>
        <input
          aria-label={t.blocks.newPlaceholder}
          placeholder={t.blocks.newPlaceholder}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <button type="submit">{t.blocks.create}</button>
      </form>
      {blocks.length === 0 ? <p className="muted">{t.blocks.empty}</p> : null}
      <ul className="blocks" aria-label={t.blocks.title}>
        {blocks.map((block) => {
          const done = block.status === 'done';
          return (
            <li key={block.id} data-block-id={block.id} data-status={block.status ?? ''}>
              <span className="kind">{t.blocks.kind[block.kind] ?? block.kind}</span>
              {editing?.id === block.id ? (
                <input
                  aria-label={t.blocks.rename}
                  autoFocus
                  value={editing.title}
                  onChange={(e) => setEditing({ id: block.id, title: e.target.value })}
                  onBlur={() => void commitRename()}
                  onKeyDown={handleEditKey}
                />
              ) : (
                <button
                  type="button"
                  className={`title${done ? ' done' : ''}`}
                  title={t.blocks.rename}
                  onClick={() => setEditing({ id: block.id, title: block.title })}
                >
                  {block.title}
                </button>
              )}
              {done ? (
                <button type="button" onClick={() => void onReopen(block)}>
                  {t.blocks.reopen}
                </button>
              ) : (
                <button type="button" onClick={() => void onComplete(block)}>
                  {t.blocks.complete}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
